import { useState, useEffect } from "react";
import { AdminLayout } from "../components/AdminLayout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { RolePreviewPanel } from "../components/RolePreviewPanel";
import {
  BarChart3,
  Users,
  FileText,
  Package,
  Building2,
  AlertCircle,
  TrendingUp,
  Star,
  CheckCircle,
  Clock,
  Shield,
  LayoutDashboard,
  Eye,
  Settings,
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../lib/AuthContext";
import { useFeatureFlags } from "../lib/FeatureFlagsContext";
import { getProducts, getClinics, getAllReviews } from "../lib/api";
import type { ProductWithStats, Clinic, ReviewWithRelations } from "../lib/types";
import { toast } from "sonner";

interface AdminDashboardProps {
  onNavigate: (page: string) => void;
}

export function AdminDashboard({ onNavigate }: AdminDashboardProps) {
  const { user, profile } = useAuth();
  const { isEnabled, toggleFeature } = useFeatureFlags();
  const [products, setProducts] = useState<ProductWithStats[]>([]);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [reviews, setReviews] = useState<ReviewWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState("overview");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [productsData, clinicsData, reviewsData] = await Promise.all([
          getProducts({}),
          getClinics({}),
          getAllReviews(),
        ]);

        setProducts(productsData);
        setClinics(clinicsData);
        setReviews(reviewsData);
      } catch (error: any) {
        console.error("Error fetching dashboard data:", error);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Check if user is admin
  if (!user || !profile || profile.role !== "admin") {
    return (
      <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
        <div className="max-w-4xl mx-auto text-center py-20">
          <Shield className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl mb-4">Access Denied</h2>
          <p className="text-muted-foreground mb-6">
            You must be an administrator to access this page
          </p>
          <Button onClick={() => onNavigate && onNavigate("discover")}>
            Back to Discover
          </Button>
        </div>
      </div>
    );
  }

  // Calculate statistics
  const stats = {
    totalProducts: products.length,
    activeProducts: products.filter(p => p.active).length,
    totalClinics: clinics.length,
    activeClinics: clinics.filter(c => c.is_active).length,
    totalReviews: reviews.length,
    pendingReviews: reviews.filter(r => r.mod_state === "pending").length,
    approvedReviews: reviews.filter(r => r.mod_state === "approved").length,
    flaggedReviews: reviews.filter(r => r.is_flagged).length,
  };

  // Recent reviews
  const recentReviews = reviews
    .sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime())
    .slice(0, 5);

  return (
    <AdminLayout currentPage="admin" onNavigate={onNavigate}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl gradient-green flex items-center justify-center">
            <LayoutDashboard className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl">Dashboard</h1>
            <p className="text-muted-foreground">
              Platform overview and management tools
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <Card key={i} className="p-6">
                <Skeleton className="h-24 w-full" />
              </Card>
            ))
          ) : (
            <>
              <Card className="p-6 glass-card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Package className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Products</p>
                    <p className="text-2xl">{stats.totalProducts}</p>
                    <p className="text-xs text-muted-foreground">
                      {stats.activeProducts} active
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 glass-card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Clinics</p>
                    <p className="text-2xl">{stats.totalClinics}</p>
                    <p className="text-xs text-muted-foreground">
                      {stats.activeClinics} active
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 glass-card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Reviews</p>
                    <p className="text-2xl">{stats.totalReviews}</p>
                    <p className="text-xs text-muted-foreground">
                      {stats.pendingReviews} pending
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 glass-card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Flagged</p>
                    <p className="text-2xl">{stats.flaggedReviews}</p>
                    <p className="text-xs text-muted-foreground">
                      Needs attention
                    </p>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList className="grid w-full grid-cols-3 glass-card mb-6">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="reviews">
                Reviews ({stats.pendingReviews})
              </TabsTrigger>
              <TabsTrigger value="products">Products</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <Card className="p-6 glass-card">
                <h3 className="text-lg mb-4">Recent Activity</h3>
                {loading ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : recentReviews.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">
                    No recent reviews
                  </p>
                ) : (
                  <div className="space-y-3">
                    {recentReviews.map((review) => (
                      <div
                        key={review.id}
                        className="p-4 rounded-xl bg-muted/30 flex items-start justify-between"
                      >
                        <div className="flex-1">
                          <p className="font-medium mb-1">
                            {review.product?.product_name || "Unknown Product"}
                          </p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>{review.stars} stars</span>
                            <span>•</span>
                            <span>by {review.author?.display_name || "Anonymous"}</span>
                            <span>•</span>
                            <span>{new Date(review.submitted_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <Badge
                          variant={
                            review.mod_state === "approved"
                              ? "default"
                              : review.mod_state === "rejected"
                              ? "destructive"
                              : "secondary"
                          }
                        >
                          {review.mod_state}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-6 glass-card">
                  <h3 className="text-lg mb-4">Review Statistics</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-primary" />
                        <span className="text-sm">Approved</span>
                      </div>
                      <span className="font-medium">{stats.approvedReviews}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-yellow-500" />
                        <span className="text-sm">Pending</span>
                      </div>
                      <span className="font-medium">{stats.pendingReviews}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        <span className="text-sm">Flagged</span>
                      </div>
                      <span className="font-medium">{stats.flaggedReviews}</span>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 glass-card">
                  <h3 className="text-lg mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <Button
                      className="w-full justify-start"
                      variant="outline"
                      onClick={() => onNavigate && onNavigate("moderation-queue")}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Review Moderation Queue
                      {stats.pendingReviews > 0 && (
                        <Badge className="ml-auto">{stats.pendingReviews}</Badge>
                      )}
                    </Button>
                    <Button
                      className="w-full justify-start gradient-green text-white hover:opacity-90"
                      onClick={() => onNavigate && onNavigate("project-status")}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      View Project Status
                    </Button>
                    <Button
                      className="w-full justify-start"
                      variant="outline"
                      onClick={() => setSelectedTab("products")}
                    >
                      <Package className="w-4 h-4 mr-2" />
                      Manage Products
                    </Button>
                    <Button
                      className="w-full justify-start"
                      variant="outline"
                      onClick={() => onNavigate && onNavigate("clinics")}
                    >
                      <Building2 className="w-4 h-4 mr-2" />
                      View Clinics
                    </Button>
                  </div>
                </Card>
              </div>

              {/* Demo & Preview Settings */}
              <Card className="p-6 glass-card border-2 border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/30 dark:bg-amber-950/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
                    <Settings className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Demo Settings</h3>
                    <p className="text-sm text-muted-foreground">
                      Configure visibility of admin tools during demos
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* DevModePanel Toggle */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <Eye className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <Label htmlFor="dev-mode-toggle" className="text-sm font-medium">
                          Role Switcher Panel
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Shows bottom-right panel for switching user roles (admin pages only)
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="dev-mode-toggle"
                      checked={isEnabled('devModePanel')}
                      onCheckedChange={() => toggleFeature('devModePanel')}
                    />
                  </div>
                  
                  {/* Status indicator */}
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`w-2 h-2 rounded-full ${isEnabled('devModePanel') ? 'bg-green-500' : 'bg-gray-400'}`} />
                    <span className="text-muted-foreground">
                      {isEnabled('devModePanel') 
                        ? 'Role switcher visible on admin pages' 
                        : 'Role switcher hidden - clean demo mode'}
                    </span>
                  </div>
                </div>
              </Card>

              {/* Role Preview Panel for selecting which role to preview */}
              <RolePreviewPanel />
            </TabsContent>

            {/* Reviews Tab */}
            <TabsContent value="reviews">
              <Card className="p-6 glass-card">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg">All Reviews</h3>
                  <Button
                    onClick={() => onNavigate && onNavigate("moderation-queue")}
                    className="gradient-green hover:opacity-90 transition-opacity"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Moderation Queue
                  </Button>
                </div>

                {loading ? (
                  <div className="space-y-3">
                    {[...Array(10)].map((_, i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {reviews.map((review) => (
                      <div
                        key={review.id}
                        className="p-4 rounded-xl bg-muted/30 flex items-start justify-between hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex-1">
                          <p className="font-medium mb-1">
                            {review.product?.product_name || "Unknown Product"}
                          </p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-3 h-3 ${
                                    i < review.stars
                                      ? "fill-current text-primary"
                                      : "text-muted-foreground"
                                  }`}
                                />
                              ))}
                            </div>
                            <span>•</span>
                            <span>by {review.author?.display_name || "Anonymous"}</span>
                            <span>•</span>
                            <span>{new Date(review.submitted_at).toLocaleDateString()}</span>
                          </div>
                          {review.title && (
                            <p className="text-sm line-clamp-1">{review.title}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {review.is_flagged && (
                            <Badge variant="outline" className="border-red-500 text-red-500">
                              Flagged
                            </Badge>
                          )}
                          <Badge
                            variant={
                              review.mod_state === "approved"
                                ? "default"
                                : review.mod_state === "rejected"
                                ? "destructive"
                                : "secondary"
                            }
                          >
                            {review.mod_state}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* Products Tab */}
            <TabsContent value="products">
              <Card className="p-6 glass-card">
                <h3 className="text-lg mb-6">All Products</h3>

                {loading ? (
                  <div className="space-y-3">
                    {[...Array(10)].map((_, i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {products.map((product) => {
                      const reviewCount = product.rating_stats?.[0]?.review_count || 0;
                      const avgStars = product.rating_stats?.[0]?.avg_stars || 0;

                      return (
                        <div
                          key={product.id}
                          className="p-4 rounded-xl bg-muted/30 flex items-center justify-between hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => onNavigate && onNavigate("product-detail", product.id)}
                        >
                          <div className="flex-1">
                            <p className="font-medium mb-1">{product.product_name}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span className="capitalize">{product.form}</span>
                              <span>•</span>
                              <span>{product.brand || "Various"}</span>
                              <span>•</span>
                              <span>SKU: {product.sku}</span>
                              {reviewCount > 0 && (
                                <>
                                  <span>•</span>
                                  <span>{avgStars.toFixed(1)} ⭐ ({reviewCount} reviews)</span>
                                </>
                              )}
                            </div>
                          </div>
                          <Badge variant={product.active ? "default" : "secondary"}>
                            {product.active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </AdminLayout>
  );
}
